import { useEffect, useRef, useState } from 'react';
import { methodOverrideRequired } from '../reviewDefaults';
import type { ReviewConfig, ReviewMethodSelection } from '../serviceExecutionApi';

export function ReviewMethodSelectionView({ selection }: { selection?: ReviewMethodSelection | null }) {
  if (!selection) return null;
  return <section aria-label="Основание выбора методики"><h4>Основание выбора методики</h4>
    <p>Типы исследований указаны исполнителем: {selection.designs.join('; ')}.</p>
    <p>Рекомендация: {selection.name || 'Нужен ручной выбор'}.</p><p>{selection.rationale}</p>
    {selection.reference && <a href={selection.reference} target="_blank" rel="noreferrer">Официальный источник рекомендации</a>}
    {selection.override_reason && <p>Решение исполнителя: {selection.override_reason}</p>}
    <p>Это рекомендация семейства и варианта методики. Готовые критерии не подставляются; применимость и оценку подтверждает исполнитель.</p>
  </section>;
}

export function ReviewMethodSelector({ value, onChange, options, onRecommend, disabled }: {
  value: ReviewConfig; onChange: (next: ReviewConfig) => void; options: string[];
  onRecommend: (designs: string[]) => Promise<ReviewMethodSelection>; disabled: boolean;
}) {
  const [designs, setDesigns] = useState(value.method_selection?.designs || []);
  const [proposal, setProposal] = useState<ReviewMethodSelection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const current = useRef(value);
  current.current = value;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  async function recommend() {
    const basis = value;
    setBusy(true);
    setError('');
    try {
      const result = await onRecommend(designs);
      if (!mounted.current) return;
      if (current.current !== basis) { setError('Настройки изменились. Запросите рекомендацию заново.'); return; }
      setProposal(result);
    } catch {
      if (mounted.current) setError('Не удалось получить рекомендацию. Повторите запрос или настройте методику вручную.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  return <fieldset className="owner-uat-stack" disabled={disabled || busy}><legend>Подобрать методику по типам исследований</legend>
    <p>Укажите типы по протоколу и материалам. Система предложит семейство методики по этим сведениям, без распознавания документов и отправки внешним сервисам.</p>
    <div className="owner-uat-stack">{options.map((name) => <label key={name}><input type="checkbox" checked={designs.includes(name)} onChange={(event) => {
      setDesigns(event.target.checked ? [...designs, name] : designs.filter((item) => item !== name));
      setProposal(null);
      setError('');
      onChange({ ...value, method_selection: null, ...(value.quality ? { quality: { ...value.quality, applicability_confirmed: false } } : {}) });
    }} /> {name}</label>)}</div>
    <button className="owner-uat-btn" type="button" disabled={!designs.length} onClick={() => void recommend()}>{busy ? 'Подбор…' : 'Предложить методику'}</button>
    {error && <p role="alert">{error}</p>}
    {proposal && <><ReviewMethodSelectionView selection={proposal} />
      <p>При применении нужно заново заполнить критерии и подтвердить применимость. Прежние критерии и ответы будут заменены пустой формой.</p>
      <button className="owner-uat-btn" type="button" onClick={() => {
        onChange({ ...value, method_selection: proposal, quality: { tool: proposal.tool || 'CUSTOM', name: proposal.name,
          version: proposal.version, reference: proposal.reference, target: value.quality?.target || '', criteria: [{ question: '', answers: ['Да', 'Нет', 'Неприменимо'] }],
          applicability_confirmed: false, ai_suggestions: false } });
        setProposal(null);
      }}>Применить рекомендацию к настройкам</button></>}
    <ReviewMethodSelectionView selection={value.method_selection} />
    {value.method_selection && <label>Причина ручного выбора или отклонения от рекомендации<textarea maxLength={3000}
      required={methodOverrideRequired(value)}
      minLength={methodOverrideRequired(value) ? 10 : undefined} value={value.method_selection.override_reason} onChange={(event) => onChange({ ...value,
        method_selection: { ...value.method_selection!, override_reason: event.target.value },
        ...(value.quality ? { quality: { ...value.quality, applicability_confirmed: false } } : {}) })} /></label>}
  </fieldset>;
}
